import React, { FC, useState, useCallback, useEffect, useMemo } from 'react';
import Select from 'react-select';

import Button from '../../components/UI/button';
import TextInput from '../../components/UI/textInput';
import CapStatus from '../status/Status';
import { useCapSession } from '../../hooks/useCapSession';
import { translate as _t } from '../../utils/translation';
import { validateEmail } from '../../utils/validation';
import { countries } from '../../types/enums';

import './confirm.scss';

interface Props {}

// Currency options for dropdown
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const CapConfirm: FC<Props> = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    amount: '',
    currency: '',
    email: '',
    emailError: '',
    firstName: '',
    lastName: '',
    country: '',
  });
  const [showWidget, setShowWidget] = useState<boolean>(false);
  const [showStatus, setShowStatus] = useState<boolean>(false);
  const [allowMerchantPayment, setAllowMerchantPayment] = useState<boolean>(true);

  // Helper to update form data
  const updateFormField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Use session hook
  const {
    sessionId,
    checkoutId,
    urlParams,
    isLoading,
    isCreatingSession,
    error,
    env,
    updateSessionAndCheckout,
    getWidgetUrl,
  } = useCapSession();

  // Initialize fields from URL params
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...(urlParams.amount && { amount: urlParams.amount }),
      ...(urlParams.currency && { currency: urlParams.currency.toUpperCase() }),
      ...(urlParams.email && { email: urlParams.email }),
    }));
  }, [urlParams.amount, urlParams.currency, urlParams.email]);

  // Get selected value for currency react-select
  const selectedCurrencyValue = useMemo(() => {
    return CURRENCY_OPTIONS.find((opt) => opt.value === formData.currency) || null;
  }, [formData.currency]);

  // Country options for react-select
  const countryOptions = useMemo(() => {
    return countries.map(c => ({ value: c.code, label: c.name }));
  }, []);

  // Get selected value for country react-select
  const selectedCountryValue = useMemo(() => {
    return countryOptions.find((opt) => opt.value === formData.country) || null;
  }, [formData.country, countryOptions]);

  // Handle email change
  const handleEmailChange = (value: string) => {
    updateFormField('email', value);

    if (value && !validateEmail(value)) {
      updateFormField('emailError', _t('Invalid email address', 'CAP.INVALID_EMAIL'));
    } else {
      updateFormField('emailError', '');
    }
  };

  // Handle amount change - numeric only
  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      updateFormField('amount', value);
    }
  };

  // Handle form submission
  const handleContinueToPayment = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const amountNum = parseFloat(formData.amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return;
      }

      // Update session with all data and get checkout_id
      const success = await updateSessionAndCheckout({
        amount: amountNum,
        currency: formData.currency,
        customer: {
          email: formData.email,
          allowMerchantPayment,
          ...(formData.firstName && { firstName: formData.firstName }),
          ...(formData.lastName && { lastName: formData.lastName }),
          ...(formData.country && { country: formData.country }),
        },
      });

      if (success) {
        setShowWidget(true);
      }
    },
    [formData, allowMerchantPayment, updateSessionAndCheckout]
  );

  const isFormValid =
    formData.amount &&
    parseFloat(formData.amount) > 0 &&
    formData.currency &&
    formData.email &&
    validateEmail(formData.email) &&
    !formData.emailError &&
    !isLoading &&
    sessionId;

  const widgetUrl = getWidgetUrl();

  // Listen for messages from widget iframe (payment completion)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: verify origin
      if (!event.origin.includes('cryptosrvc.com')) {
        return;
      }

      console.log('Widget message received:', event.data);

      // Handle payment success
      if (event.data.type === 'CAP_PAYMENT_SUCCESS') {
        console.log('Payment successful, showing status');
        setShowStatus(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Show status page after payment completion
  if (showStatus && sessionId && checkoutId) {
    return (
      <CapStatus
        sessionId={sessionId}
        checkoutId={checkoutId}
        env={urlParams.env || 'dev'}
      />
    );
  }

  // Error state - missing merchantId
  if (!urlParams.merchantId) {
    return (
      <div className="cap-confirm">
        <div className="cap-confirm-container">
          <div className="cap-confirm-error">
            <h2>{_t('Invalid Request', 'CAP.INVALID_REQUEST')}</h2>
            <p>{_t('Merchant ID is required', 'CAP.MERCHANT_ID_REQUIRED')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - creating session
  if (isCreatingSession) {
    return (
      <div className="cap-confirm">
        <div className="cap-confirm-container">
          <div className="cap-confirm-loading">
            <p>{_t('Creating session...', 'CAP.CREATING_SESSION')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - session creation failed
  if (error && !sessionId) {
    return (
      <div className="cap-confirm">
        <div className="cap-confirm-container">
          <div className="cap-confirm-error">
            <h2>{_t('Configuration Error', 'CAP.SESSION_ERROR')}</h2>
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cap-confirm">
      <div className="cap-confirm-container">
        {!showWidget ? (
          <>
            <h1 className="cap-confirm-title">
              {_t('VERIFY YOUR IDENTITY', 'CAP.VERIFY_IDENTITY')}
            </h1>

            <p className="cap-confirm-subtitle">
              {_t('Complete verification to proceed with payment', 'CAP.COMPLETE_KYC')}
            </p>

            {error && <div className="cap-confirm-error-message">{error}</div>}

            <div className="payment-info-summary">
              <h3 className="payment-info-title">
                {_t('Payment Details', 'CAP.PAYMENT_DETAILS')}
              </h3>
              <div className="payment-info-row">
                <span className="payment-info-label">{_t('Amount', 'CAP.AMOUNT')}</span>
                <span className="payment-info-value">
                  {formData.amount || urlParams.amount || '0.00'} {formData.currency || urlParams.currency?.toUpperCase() || ''}
                </span>
              </div>
            </div>

            <form className="cap-confirm-form">
              <TextInput
                label={_t('Amount', 'CAP.AMOUNT')}
                value={formData.amount}
                onChange={handleAmountChange}
                size={"small"}
                name="amount"
                placeholder="0.00"
                required
                disabled={!!urlParams?.amount}
              />

              <div className={`custom-select-wrapper ${!!urlParams?.currency ? 'disabled' : ''}`}>
                <label className="custom-select-label">
                  {_t('Currency', 'CAP.CURRENCY')}*
                </label>
                <Select
                  value={selectedCurrencyValue}
                  onChange={(option) => updateFormField('currency', option?.value || '')}
                  options={CURRENCY_OPTIONS}
                  placeholder={_t('Select currency', 'CAP.SELECT_CURRENCY')}
                  className="cap-custom-select-container"
                  classNamePrefix="custom-select"
                  isClearable
                  isSearchable={false}
                  isDisabled={!!urlParams?.currency}
                />
              </div>

              <TextInput
                label={_t('Email Address', 'CAP.EMAIL_ADDRESS')}
                value={formData.email}
                onChange={handleEmailChange}
                size={"small"}
                name="email"
                detailsError={formData.emailError}
                required
                type="email"
                disabled={!!urlParams?.email}
              />

              <TextInput
                label={_t('First Name', 'CAP.FIRST_NAME')}
                value={formData.firstName}
                onChange={(value) => updateFormField('firstName', value)}
                size={"small"}
                name="firstName"
              />

              <TextInput
                label={_t('Last Name', 'CAP.LAST_NAME')}
                value={formData.lastName}
                onChange={(value) => updateFormField('lastName', value)}
                size={"small"}
                name="lastName"
              />

              <div className="custom-select-wrapper">
                <label className="custom-select-label">
                  {_t('Country', 'CAP.COUNTRY')}
                </label>
                <Select
                  value={selectedCountryValue}
                  onChange={(option) => updateFormField('country', option?.value || '')}
                  options={countryOptions}
                  placeholder={_t('Select country', 'CAP.SELECT_COUNTRY')}
                  className="cap-custom-select-container"
                  classNamePrefix="custom-select"
                  isClearable
                  isSearchable
                />
              </div>

              <div className="merchant-agreement">
                <p className="merchant-agreement-title">
                  {_t('Merchant Agreement', 'CAP.MERCHANT_AGREEMENT')}
                </p>

                <label className={`merchant-option ${allowMerchantPayment ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="allowMerchantPayment"
                    checked={allowMerchantPayment}
                    onChange={() => setAllowMerchantPayment(true)}
                  />
                  <div className="merchant-option-content">
                    <span className="merchant-option-title">
                      {_t('Yes, proceed my payment to this merchant', 'CAP.PROCEED_TO_MERCHANT')}
                    </span>
                    <span className="merchant-option-description">
                      {_t('Send crypto directly to the merchant address', 'CAP.SEND_TO_MERCHANT')}
                    </span>
                  </div>
                </label>

                <label className={`merchant-option ${!allowMerchantPayment ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="allowMerchantPayment"
                    checked={!allowMerchantPayment}
                    onChange={() => setAllowMerchantPayment(false)}
                  />
                  <div className="merchant-option-content">
                    <span className="merchant-option-title">
                      {_t('No, hold crypto on Platform', 'CAP.HOLD_ON_PLATFORM')}
                    </span>
                    <span className="merchant-option-description">
                      {_t("We'll create an account and credit funds for you", 'CAP.CREATE_ACCOUNT')}
                    </span>
                  </div>
                </label>
              </div>

              <Button
                onClick={handleContinueToPayment}
                isDisabled={!isFormValid}
                size={"small"}
                className="cap-confirm-submit-button"
              >
                {isLoading
                  ? _t('Loading...', 'CAP.LOADING')
                  : _t('Continue to Payment', 'CAP.CONTINUE_TO_PAYMENT')}
              </Button>

              <p className="security-notice">
                {_t(
                  'Your data is encrypted and secure. We never share your information with third parties.',
                  'CAP.SECURITY_NOTICE'
                )}
              </p>
            </form>
          </>
        ) : (
          <div className="cap-confirm-widget-container">
            <h2 className="widget-title">
              {_t('Complete Your Payment', 'CAP.COMPLETE_PAYMENT')}
            </h2>

            {widgetUrl && (
              <iframe
                id="cap-payment-widget"
                src={widgetUrl}
                width="100%"
                height="600px"
                frameBorder="0"
                allow="payment"
                title="CAP Payment Widget"
                className="cap-widget"
              />
            )}

            <Button
              onClick={() => setShowWidget(false)}
              size={"small"}
              className="cap-back-button"
            >
              {_t('Back', 'CAP.BACK')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapConfirm;
